import React, { Fragment, useEffect, useState } from "react";
import { CountryNames, SubjectNames, postData } from "@/Action/api";
import Link from "next/link";

/**
 * Enquiry form used across the country / study-destination pages.
 *
 * Fields mirror the main enquiry form in Footersection so every form on a
 * country page asks the same questions: Name, Code + Mobile, Preferred Country,
 * Preferred Study Level and the three consent checkboxes.
 *
 * @param column               Bootstrap column width per field (6 renders two-up).
 * @param Text                 Heading colour suffix — "white" on dark panels.
 * @param isRemoveAreaCode     Hide the country-code select (tight hero layout).
 * @param defaultDestinationId Country pre-selected in "Preferred Country".
 */
const SmallContactSection = ({
  column = 12,
  isFull = true,
  Text = "black",
  isRemoveAreaCode = false,
  defaultDestinationId = "",
}) => {
  const [isloading, setisloading] = useState(false);
  const [options, setOptions] = useState({});
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    mobile: "",
    countrycode: "91",
    destinationId: defaultDestinationId ? String(defaultDestinationId) : "",
    plantime: "2026",
    nearestOffice: "Chandigarh",
    mode: "Virtual",
    studylevel: "1",
    fund: "By job",
    terms: true,
    contactallow: true,
    offerallow: true,
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstname.trim()) {
      nextErrors.firstname = "Please enter your name.";
    }
    if (!formData.mobile.trim()) {
      nextErrors.mobile = "Please enter your mobile number.";
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      nextErrors.mobile = "Please enter a valid 10-digit mobile number.";
    }
    if (!formData.destinationId) {
      nextErrors.destinationId = "Please select a preferred country.";
    }
    if (!formData.studylevel) {
      nextErrors.studylevel = "Please select a preferred study level.";
    }
    if (!formData.terms) {
      nextErrors.terms = "Please accept the TIG Terms and privacy policy.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const result = await postData("enquiry/save", formData);
      if (result?.message) {
        setFormData({
          firstname: "",
          lastname: "",
          email: "",
          mobile: "",
          countrycode: "91",
          destinationId: defaultDestinationId
            ? String(defaultDestinationId)
            : "",
          plantime: "2026",
          nearestOffice: "Chandigarh",
          mode: "Virtual",
          studylevel: "1",
          fund: "By job",
          terms: true,
          contactallow: true,
          offerallow: true,
        });
        window.alert(result?.message);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Country pages pass their own country so "Preferred Country" starts on it.
  useEffect(() => {
    if (!defaultDestinationId) return;
    setFormData((prev) => ({
      ...prev,
      destinationId: String(defaultDestinationId),
    }));
  }, [defaultDestinationId]);

  useEffect(() => {
    (async () => {
      try {
        setisloading(true);
        const result = await CountryNames();
        const result2 = await SubjectNames();

        if (result?.res?.success === true) {
          setisloading(false);

          setOptions((prev) => ({
            ...prev,
            destinationId: result?.res?.data,
            studylevel: result2?.res?.data,
          }));
        }
      } catch (err) {
        setisloading(false);

        console.error(err);
      }
    })();
  }, []);

  if (isloading) return <div>Loading......</div>;
  return (
    <Fragment>
      <div className="">
        <div className="row">
          <div className={`col-12 `}>
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Name */}
                <div
                  className={`col-${column} SmallContactSection custom-grid-item course-field mb--15`}
                >
                  <h6 className={`field-1 text-${Text} mb--10`}>Name*</h6>
                  <input
                    type="text"
                    name="firstname"
                    placeholder="Your Name"
                    className="bg-white"
                    required
                    value={formData.firstname}
                    onChange={handleChange}
                  />
                  {errors.firstname && (
                    <small className="text-danger">{errors.firstname}</small>
                  )}
                </div>

                {/* Country code + Mobile number */}
                <div
                  className={`col-${column} SmallContactSection row`}
                  style={{
                    paddingRight: "0px",
                  }}
                >
                  {!isRemoveAreaCode && (
                    <div className="course-field col-3 col-md-2">
                      <h6 className={`text-${Text} mb--10`}>Code</h6>
                      <div className="rbt-modern-select bg-transparent height-30 w-100 mb--10">
                        <select
                          className="w-100"
                          style={{
                            height: "50px",
                          }}
                          name="countrycode"
                          value={formData.countrycode}
                          onChange={handleChange}
                        >
                          <option value="91">91</option>
                          <option value="01">01</option>
                          <option value="92">92</option>
                          <option value="93">93</option>
                          <option value="94">94</option>
                          <option value="95">95</option>
                          <option value="96">96</option>
                          <option value="97">97</option>
                          <option value="98">98</option>
                          <option value="99">99</option>
                        </select>
                      </div>
                    </div>
                  )}
                  <div
                    className={`custom-grid-item col-${
                      isRemoveAreaCode ? "12" : "9"
                    } col-md-${
                      isRemoveAreaCode ? "12" : "10"
                    } course-field mb--15`}
                    style={{
                      paddingRight: "0px",
                    }}
                  >
                    <h6 className={`field-1 text-${Text} mb--10`}>
                      Mobile Number*
                    </h6>
                    <input
                      type="text"
                      name="mobile"
                      placeholder="Mobile Number"
                      className="bg-white"
                      required
                      value={formData.mobile}
                      onChange={handleChange}
                    />
                    {errors.mobile && (
                      <small className="text-danger">{errors.mobile}</small>
                    )}
                  </div>
                </div>

                {/* Preferred Country */}
                <div
                  className={`col-${column} SmallContactSection course-field mb--15`}
                >
                  <h6 className={`text-${Text} mb--10`}>Preferred Country*</h6>
                  <select
                    name="destinationId"
                    className="w-100"
                    required
                    value={formData.destinationId}
                    onChange={handleChange}
                    style={{
                      height: "50px",
                    }}
                  >
                    <option value="" disabled>
                      ----- Select Country ------
                    </option>
                    {options?.destinationId?.map((data) => (
                      <option key={data.id} value={data.id}>
                        {data.name}
                      </option>
                    ))}
                  </select>
                  {errors.destinationId && (
                    <small className="text-danger">{errors.destinationId}</small>
                  )}
                </div>

                {/* Preferred Study Level */}
                <div
                  className={`col-${column} SmallContactSection course-field mb--15`}
                >
                  <h6 className={`text-${Text} mb--10`}>
                    Preferred Study Level*
                  </h6>
                  <select
                    name="studylevel"
                    className="w-100"
                    required
                    value={formData.studylevel}
                    onChange={handleChange}
                    style={{
                      height: "50px",
                    }}
                  >
                    {options?.studylevel?.map((data) => (
                      <option key={data.id} value={data.id}>
                        {data.name}
                      </option>
                    ))}
                  </select>
                  {errors.studylevel && (
                    <small className="text-danger">{errors.studylevel}</small>
                  )}
                </div>

                {/* Consent checkboxes */}
                <div className="col-12 course-field pb--0">
                  <p className="rbt-checkbox-wrapper mb--5 d-flex">
                    <input
                      id="rbt-checkbox-1"
                      name="rbt-checkbox-1"
                      type="checkbox"
                      className="h-auto w-auto opacity-inherit static checkbox_design_layout "
                      checked={formData?.terms}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          terms: e.target.checked,
                        }));
                        setErrors((prev) => ({ ...prev, terms: "" }));
                      }}
                    />
                    <label className="visiblefinder">
                      I agree to{" "}
                      <span className="text-underline ">TIG Terms</span> and{" "}
                      <span className="text-underline cursor-pointer">
                        <Link href={"/privacy-policy"}>privacy policy</Link>{" "}
                      </span>
                    </label>
                  </p>
                  {errors.terms && (
                    <small className="text-danger">{errors.terms}</small>
                  )}
                </div>
                <div className="col-12 course-field pb--0 d-flex">
                  <p className="rbt-checkbox-wrapper mb--5 d-flex">
                    <input
                      id="rbt-checkbox-2"
                      type="checkbox"
                      name="contactallow"
                      className="h-auto w-auto opacity-inherit static checkbox_design_layout"
                      checked={formData.contactallow}
                      onChange={handleChange}
                    />
                    <label className="visiblefinder">
                      Please contact me by phone, email, or SMS to assist with
                      my enquiry
                    </label>
                  </p>
                </div>
                <div className="col-12 course-field pb--10 d-flex">
                  <p className="rbt-checkbox-wrapper mb--5 d-flex">
                    <input
                      id="rbt-checkbox-3"
                      type="checkbox"
                      name="offerallow"
                      className="h-auto w-auto opacity-inherit static checkbox_design_layout"
                      checked={formData.offerallow}
                      onChange={handleChange}
                    />
                    <label className="visiblefinder">
                      I would like to receive updates and offers from TIG
                    </label>
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="d-flex mt-4 justify-content-start">
                <button
                  type="submit"
                  className="rbt-btn rbt-switch-btn btn-gradient btn-sm cursor-pointer"
                >
                  <span>Help me study abroad</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default SmallContactSection;
